import { Component, computed, EventEmitter, input, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Question } from '../models';

@Component({
    selector: 'fgai-progress',
    imports: [CommonModule],
    templateUrl: './progress.html',
    styleUrl: './progress.scss',
})
export class Progress {
    @Input()
    active = 1;

    questions = input<Question[]>([]);

    value = input<{ [number: number]: boolean }>({});

    @Output()
    jump = new EventEmitter<number>();

    activePopover: number | undefined = undefined;

    /**
     * This is the current popovers which should be shown if popovers are enabled;
     * if they become enabled after a timeout, show this one
     */
    currentPopover: string | undefined;
    /**
     * This is the popover which we want to switch to from the current popover
     */
    nextPopover: string | undefined;
    /**
     * Only after a short timeout should popovers be shown;
     * to prevent them quickly appearing/disappearing when shortly mousing over them
     */
    private popoversEnabled = false;
    /**
     * Moused out before the timer was done
     */
    private mousedOut = false;
    /**
     * In Firefox a mouseleave is triggered on a view transition: disable the disable!
     */
    private preventDisable = false;
    /**
     * Don't trigger a new transition when one is already ongoing.
     * After the transition is done, check if a new popover has been set
     */
    transitioning = false;
    /**
     * First popover being shown should animate in, reset to 0 after popovers are hidden
     */
    popoverCount = 0;

    lastAnswered = computed(() => {
        const answered = this.value();
        let lastAnswered = 1;
        for (const question of this.questions()) {
            if (question.sequence_number > lastAnswered && answered[question.sequence_number]) {
                lastAnswered = question.sequence_number;
            }
        }
        return lastAnswered;
    });

    delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async enablePopovers() {
        this.mousedOut = false;
        await this.delay(200);
        if (!this.mousedOut) {
            this.popoversEnabled = true;
            if (this.nextPopover) {
                this.activatePopover(this.nextPopover, true);
            }
        }
    }

    disablePopovers(force = false) {
        if (!force && this.preventDisable) {
            this.preventDisable = false;
            return;
        }

        this.activePopover = undefined;
        this.popoverCount = 0;
        this.popoversEnabled = false;
        this.mousedOut = true;
        this.nextPopover = undefined;
        if (this.currentPopover) {
            document.getElementById(this.currentPopover)?.hidePopover();
            this.currentPopover = undefined;
        }
    }

    async activatePopover(elementId: string, always = false) {
        if (!always && elementId === this.currentPopover) {
            return;
        }
        this.nextPopover = elementId;
        if (this.transitioning || !this.popoversEnabled) {
            return;
        }

        this.transitioning = true;
        do {
            this.popoverCount++;
            const current = this.currentPopover;
            const next = this.nextPopover;

            this.currentPopover = next;
            this.nextPopover = undefined;
            this.preventDisable = true;
            this.activePopover = parseInt(this.currentPopover.replace(/[^0-9]/g, ''));
            const transition = document.startViewTransition(async () => {
                if (current && current !== next) {
                    document.getElementById(current)?.hidePopover();
                }

                document.getElementById(next)?.showPopover();
            });
            await transition.ready;
        }
        while (this.nextPopover)

        this.transitioning = false;
    }

    jumpTo(number: number) {
        this.disablePopovers(true);
        this.jump.next(number);
        this.enablePopovers();
        return false; // magic needed to allow the popup to close
    }
}
